/**
 * Autotest surface contract for the logged-in **FocusedNav** (activation
 * population). Step 8 — AT-prep (plugs-and-sockets) + A/A launch. Modeled on the
 * SUTD bulk-download page socket AT (`AutotestSutdBulkDownload.ts`): `Surfaces` =
 * the slots a variant may override, `CONTROL` = the single source of every
 * default (today's shipped nav), `resolveSurfaces(arm, args)` returns the active
 * arm's overrides merged over CONTROL.
 *
 * This PR wires the sockets to the full variant test (`nav_activation_2026_q2_v1`,
 * control + 12) with `ARMS` EMPTY, so all 13 arms render byte-identically —
 * validating the bucketing + variant-delivery path on the socketed nav before
 * Step 9 fills `ARMS`.
 *
 * Divergence from the SUTD page AT (intentional): the nav is **SSR-prerendered**,
 * so a client-side bucket would flash. The arm is bucketed **server-side** in
 * `FocusedNavPresenter` and threaded in as the `navArm` prop, so `resolveSurfaces`
 * takes the arm as a parameter rather than calling the generated `abTest()`.
 */

import { TEST_CONFIG } from 'js/ab_tests/tests/nav_activation_2026_q2_v1';

// === Arms (control + 12) ===

export type Arm =
  | 'control'
  | 'v_cta_emphasis' // A1: emphasized premium CTA
  | 'v_cta_price' // B1: trial price in the button face
  | 'v_cta_value_prop' // B2: value-prop copy instead of price/trial
  | 'v_cta_lock_border' // B3: lock_border icon
  | 'v_cta_pro_badge' // B4: alternative premium signifier
  | 'v_mentions_count' // C1: numeric count instead of the binary dot
  | 'v_mentions_cited_label' // C2: "See who cited you" label
  | 'v_mentions_cta' // C3: render the mentions item as a direct premium CTA
  | 'v_social_proof' // C4: live social-proof element near the CTA
  | 'v_account_upgrade_button' // D2: account-menu upgrade anchor as a styled button
  | 'v_account_avatar_upsell' // D3: upsell affordance on the account avatar
  | 'v_mobile_premium_tab'; // E1: mobile upsell as a bottom tab instead of top icon

/** Resolved arm. `undefined` = holdout / not bucketed → CONTROL. */
export type ResolvedArm = Arm | undefined;

// === Render-time inputs (so arms can quantify / anchor) ===

export type SurfaceArgs = {
  isPremium: boolean;
  isMobileNav: boolean;
  pageName: string;
  /** premium_upsells.trialPricingCopy — the trial CTA copy, or null. */
  trialPricingCopy: string | null;
  /** Unseen mentions count (already fetched client-side). */
  mentionsCount: number;
  /** NEW [Step 9 data] numeric trial price for the in-button price variant (B1). */
  trialPriceLabel: string | null;
  /** NEW [Step 9 data] "N reading now / joined this week" for social proof (C4). */
  liveReaderCount: number | null;
};

// === Surfaces ===

export type CtaEmphasis = 'default' | 'accent';
export type CtaIcon = 'lock' | 'lock_border' | 'pro_badge';
export type MentionsBadge = 'dot' | 'count';
export type MobilePremiumPlacement = 'top_icon' | 'bottom_tab';

export type Surfaces = {
  /** A1. Visual treatment of the prominent premium CTA. */
  ctaEmphasis: CtaEmphasis;
  /** B1/B2. Premium CTA label. `null` = the component's offer-derived default
   * (trial/price copy), which can't be a static constant. */
  ctaLabel: string | null;
  /** B3/B4. The premium signifier icon on the CTA. */
  ctaIcon: CtaIcon;
  /** C2. Mentions nav-item label. */
  mentionsLabel: string;
  /** C1. Mentions unread indicator — binary dot vs numeric count. */
  mentionsBadge: MentionsBadge;
  /** C3. Render the mentions item as a direct premium CTA instead of icon→page. */
  mentionsAsCta: boolean;
  /** C4. Optional live social-proof line near the CTA. `null` = none (CONTROL). */
  socialProofSlot: string | null;
  /** D2. Account-menu upgrade anchor — plain link vs styled button. */
  accountUpgradeStyle: 'link' | 'button';
  /** D3. Upsell affordance (badge + mini-CTA) on the account avatar, non-premium. */
  accountAvatarUpsell: boolean;
  /** E1. Mobile premium placement — top-right icon vs bottom tab-bar tab. */
  mobilePremiumPlacement: MobilePremiumPlacement;
};

/** CONTROL is the single source of every default — today's shipped nav. */
export const CONTROL: Surfaces = {
  ctaEmphasis: 'default',
  ctaLabel: null,
  ctaIcon: 'lock',
  mentionsLabel: 'Mentions',
  mentionsBadge: 'dot',
  mentionsAsCta: false,
  socialProofSlot: null,
  accountUpgradeStyle: 'link',
  accountAvatarUpsell: false,
  mobilePremiumPlacement: 'top_icon',
};

// === ARMS ===
// EMPTY in the AT-prep / A/A PR — every arm → CONTROL → byte-identical rendering.
// Step 9 fills this with the 12 variant overrides, keyed by the arm names above.
type ArmSpec = (args: SurfaceArgs) => Partial<Surfaces>;

const ARMS: Partial<Record<Arm, ArmSpec>> = {};

/**
 * Returns the arm's surface overrides merged over CONTROL. `arm` is the
 * server-bucketed value threaded from FocusedNavPresenter (or 'control'/undefined
 * for users not in the test). With ARMS empty this is always CONTROL.
 */
export function resolveSurfaces(
  arm: ResolvedArm,
  args: SurfaceArgs,
): Surfaces & { arm: ResolvedArm } {
  const spec = arm !== undefined ? ARMS[arm] : undefined;
  const overrides = spec ? spec(args) : {};
  return { arm, ...CONTROL, ...overrides };
}

/** Tag an impression with the resolved arm for per-arm attribution. */
export function impressionExtra(arm: ResolvedArm): Record<string, unknown> {
  const isActive: boolean = TEST_CONFIG.active as boolean;
  return {
    autotest_bucket: arm ?? 'control',
    ...(isActive && { autotest: 'nav_activation_2026_q2_v1' }),
  };
}
